import React from 'react'
import {
  Stack,
  Box,
  Typography,
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  useTheme,
  alpha,
} from '@mui/material'
import { Close as CloseIcon, CreditCardOff as CreditCardOffIcon } from '@mui/icons-material'
import { useSuspenseQuery, useQueryClient } from '@tanstack/react-query'

import { HttpError } from '@/api/http'
import { createPaymentCreditCardsQueryOptions } from '@/api/payment/queries'
import { useMutationDeletePaymentCreditCard } from '@/api/payment/mutations'

import { useToast } from '@/contexts/ToastContext'
import { useDisclosure } from '@/hooks/useDisclosure'
import { CreditCardItem } from './CreditCardItem'

export const CardsList: React.FC = () => {
  const theme = useTheme()
  const queryClient = useQueryClient()
  const { show } = useToast()
  const confirmModal = useDisclosure()

  const { data: creditCards } = useSuspenseQuery(createPaymentCreditCardsQueryOptions({ infinite: true }))
  const { mutateAsync: mutateDeletePaymentCreditCard } = useMutationDeletePaymentCreditCard()
  const [deletingCardIds, setDeletingCardIds] = React.useState<number[]>([])
  const [cardToDelete, setCardToDelete] = React.useState<number | null>(null)

  const handleDeleteCard = (cardId: number) => {
    setCardToDelete(cardId)
    confirmModal.open()
  }

  const handleConfirmDelete = () => {
    if (cardToDelete === null) return

    setDeletingCardIds((prev) => [...prev, cardToDelete])
    confirmModal.close()

    mutateDeletePaymentCreditCard({ id: cardToDelete })
      .then(() => {
        queryClient.invalidateQueries({ queryKey: ['payment-credit-cards'] })

        show('O cartão foi removido com sucesso!', {
          severity: 'success',
        })
      })
      .catch((error: HttpError) => {
        show(error.message || 'Ocorreu um erro ao remover o cartão.', {
          severity: 'error',
        })
      })
      .finally(() => {
        setDeletingCardIds((prev) => prev.filter((id) => id !== cardToDelete))
        setCardToDelete(null)
      })
  }

  const handleCloseModal = () => {
    confirmModal.close()
    setCardToDelete(null)
  }

  if (!creditCards?.results?.length) {
    return (
      <Stack spacing={1} paddingY={5} width="100%" textAlign="center">
        <Stack direction="column" alignItems="center" spacing={4}>
          <Box component="img" src="/assets/png/empty.png" width="100%" maxWidth={300} />
          <Stack direction="column" alignItems="center" spacing={1}>
            <Typography variant="h6" color="textSecondary">
              Ops! Parece que você não tem cartões cadastrados.
            </Typography>
            <Typography variant="body1" color="textSecondary">
              Adicione um cartão para começar.
            </Typography>
          </Stack>
        </Stack>
      </Stack>
    )
  }

  return (
    <>
      <Stack direction="column" width="100%" spacing={1}>
        {creditCards.results.map((card) => (
          <CreditCardItem
            key={card.id}
            card={card}
            onDelete={handleDeleteCard}
            isDeleting={deletingCardIds.includes(card.id)}
          />
        ))}
      </Stack>

      <Dialog
        open={confirmModal.isOpen}
        onClose={handleCloseModal}
        fullWidth
        maxWidth="xs"
        slotProps={{
          paper: {
            sx: { borderRadius: 3, padding: 1, minWidth: 320 },
          },
        }}
      >
        <IconButton
          aria-label="fechar"
          onClick={handleCloseModal}
          sx={{
            position: 'absolute',
            right: 4,
            top: 4,
          }}
        >
          <CloseIcon color="disabled" sx={{ fontSize: '16px' }} />
        </IconButton>

        <DialogContent>
          <Stack spacing={2.5} alignItems="center">
            <Stack alignItems="center" spacing={1.5} sx={{ mt: 1 }}>
              <Box
                sx={{
                  width: 72,
                  height: 72,
                  borderRadius: '50%',
                  bgcolor: alpha(theme.palette.error.main, 0.12),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <CreditCardOffIcon sx={{ fontSize: 36, color: 'error.main' }} />
              </Box>
              <Typography variant="h6" fontWeight="700" textAlign="center" color="text.primary">
                Remover cartão?
              </Typography>
              <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ px: 1 }}>
                Tem certeza que deseja remover este cartão? Esta ação não poderá ser desfeita.
              </Typography>
            </Stack>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ pb: 2.5 }}>
          <Stack width="100%" direction="column" spacing={1}>
            <Button
              color="error"
              variant="contained"
              onClick={handleConfirmDelete}
              sx={{ width: '100%', borderRadius: 4 }}
              disableElevation
            >
              Remover
            </Button>
            <Button
              color="primary"
              variant="outlined"
              onClick={handleCloseModal}
              sx={{ width: '100%', borderRadius: 4 }}
              disableElevation
            >
              Cancelar
            </Button>
          </Stack>
        </DialogActions>
      </Dialog>
    </>
  )
}
